<template>
  <div class="aia-layout" :key="query_version">
    <div class="aia-header">
      <v-progress-circular
        v-if="loading"
        indeterminate
        color="primary"
      />

      <div>
        <ViewTitle title="Configuración de IA para la empresa"/>
      </div>
    </div>

    <div class="aia-layout-dual">

      <div class="aia-layout-body">
        <h2 v-if="loading">
          Cargando configuración...
        </h2>

        <div v-if="ai !== null">

          <div class="aia-butrow">
            <v-btn
              color="primary"
              :disabled="!changed"
              @click="save"
            >
              Guardar
            </v-btn>

            <v-btn
              color="red"
              :disabled="!changed"
              @click="reload"
            >
              Descartar
            </v-btn>
          </div>

          <div class="aia-select">
            <Select :props="model_select_props" @update="model_select_change" />
          </div>

          <div class="aia-select">
            <AutocompleteSelect :props="processes_select_props" @update="processes_select_change" />
          </div>
          
        </div>
      </div>

      <div class="aia-layout-panel2">
        <div class="aia-documentation">
          <h2>¿Cómo construye la IA sus respuestas?</h2>
          <p class="mt-2">La IA es consultada para dar una recomendación de que hacer en base a los siguientes datos:</p>
          <ul>
            <li><b>Expediente seleccionado.</b> Datos del expediente y historial</li>
            <li><b>BP.</b> Nombre y descripción</li>
            <li><b>BP.</b> Campos de la transición</li>
            <li><b>Usuario.</b> Configuración de idioma</li>
            <li>Conocimiento interno del modelo de IA</li>
          </ul>
        </div>

        <div class="aia-history">
          <h2>Historial de uso</h2>
          <p>[Funcionalidad pendiente de desarrollo]</p>
        </div>
      </div>

    </div>

    <ConfirmDialog ref="confirm_dialog" />
  </div>
</template>


<style lang="scss">
 @import "@/sass/variables.scss";

 .aia-horizontal {
   display: flex;
   flex-direction: row;
   gap: 8px;
 }

 .aia-layout {
   display: flex;
   flex-direction: column;
   padding: 18px;
   width: 100%;
   min-height: 90%;
 }

 .aia-layout-dual {
   margin-top: 24px;
   display: flex;
   flex-direction: row;
   flex-wrap: wrap;
   gap: 48px;
 }

 .aia-layout-body {
   display: flex;
   flex-direction: column;
   flex-wrap: nowrap;
   gap: 24px;
   min-width: 320px;
   max-width: 480px;
   flex-grow: 4;
 }

 .aia-layout-panel2 {
   display: flex;
   flex-direction: column;
   flex-wrap: nowrap;
   gap: 24px;
   min-width: 320px;
   max-width: 100%;
 }

 .aia-header {
   display: flex;
   flex-direction: row;
   padding: 0px;
   gap: 16px;
   align-items: center;
 }

 .aia-butrow {
   display: flex;
   flex-direction: row;
   flex-wrap: wrap;
   justify-content: space-between;
   width: 100%;
   gap: 24px;
   padding-bottom: 24px;
 }

 .aia-select {
   margin-top: 12px;
   min-width: 240px;
   max-width: 600px;
 }
</style>


<script>
 import { mapState, mapActions, mapMutations, mapGetters } from 'vuex';
 import AIAPI from '@/api/AI';
 import ConfirmDialog from '@/components/ConfirmDialog';
 import ViewTitle from '@/components/ViewTitle';
 import Select from '@/components/inputs/Select';
 import AutocompleteSelect from '@/components/inputs/AutocompleteSelect';

 const NOOP = (() => {});

 function copy(data) {
   return JSON.parse(JSON.stringify(data));
 }

 export default {
   name: 'AIAdmin',

   components: {
     ConfirmDialog,
     ViewTitle,
     Select,
     AutocompleteSelect,
   },

   data: () => ({
     loading: false,
     ai: null,
     ai_model: 'n',
     ai_allowed_processes: [],
     query_version: 1,
     changed: false,
   }),

   computed: {
     ...mapGetters(['centro_id']),
     ...mapState(['processes']),

     processes_items() {
       const procmap = this.processes;

       function pathify(p) {
         if (p.parent && procmap[p.parent] !== undefined)
           return pathify(procmap[p.parent]).concat([p.id]);
         return [p.id];
       }

       function path_names(p) {
         return pathify(p).map(pid => procmap[pid].name).join('/');
       }

       return ((Object.values(procmap) || [])
         .map(proc => [
           proc.id,
           `${proc.id} [${proc.standard_name}] ${path_names(proc)}`
         ])
       );
     },

     processes_select_props() {
       return {
         items: this.processes_items,
         value: this.ai_allowed_processes,
         label: "Procesos visibles para la IA",
         multiple: true,
         required: false,
       }
     },

     model_select_props() {
       return {
         items: [
           ['n', 'Deshabilitado'],
           ['l', 'Local (NO DISPONIBLE)'],
           ['i', 'IAIA'],
         ],
         value: this.ai_model,
         label: "Modelo de IA",
         multiple: false,
         required: true,
       }
     },
   },

   async mounted() {
     await this.reload();
   },

   methods: {
     model_select_change(value) {
       this.ai_model = value;
       this.changed = true;
     },

     processes_select_change(value) {
       this.ai_allowed_processes = value;
       this.changed = true;
     },

     async reload() {
       this.loading = true;
       await this.query_settings();
       await this.set_current(this.ai);
       this.loading = false;
       this.changed = false;
     },

     async query_settings() {
       this.ai = await AIAPI.site_settings(this.centro_id);
       this.query_version++;
     },

     async set_current(ai) {
       ai = ai || {};
       this.ai_model = ai.model || 'n';
       this.ai_allowed_processes = ai.allowed_processes || 'n';
       await this.$nextTick();
     },

     async save() {
       const payload = {
         model: this.ai_model,
         allowed_processes: this.ai_allowed_processes,
       };
       try {
         const req = await AIAPI.update_site_settings(this.centro_id, payload);
       } catch (err) {
         console.error(err);
       }
       await this.reload();
     },
   },
 }
</script>
